import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { CheckCircle, XCircle, RefreshCw } from "lucide-react";

export default function Settings() {
  const [settings, setSettings] = useState({});
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await adminApi.getSettings();
      setSettings(data.settings || {});
      setProviders(data.providers || []);
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleProviderChange = async (value) => {
    setSaving(true);
    try {
      await adminApi.updateSetting("ai_provider", value);
      setSettings({ ...settings, ai_provider: { ...settings.ai_provider, value } });
      toast.success(`AI Provider changed to ${value === "gemini" ? "Google Gemini" : "BytePlus Seedream"}`);
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const handleModelChange = async (provider, value) => {
    const key = provider === "gemini" ? "gemini_model" : "byteplus_model";
    setSaving(true);
    try {
      await adminApi.updateSetting(key, value);
      setSettings({ ...settings, [key]: { ...settings[key], value } });
      toast.success("Model updated");
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const handleInitSettings = async () => {
    try {
      const result = await adminApi.initSettings();
      toast.success(`Initialized ${result.total} settings`);
      fetchSettings();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const currentProvider = settings.ai_provider?.value || "gemini";
  const currentGeminiModel = settings.gemini_model?.value || "gemini-2.0-flash-exp-image-generation";
  const currentByteplusModel = settings.byteplus_model?.value || "seedream-3-0-t2i-250117";

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-gray-500">Configure AI image generation provider</p>
        </div>
        <Button variant="outline" onClick={handleInitSettings}>
          <RefreshCw className="w-4 h-4 mr-2" /> Initialize Defaults
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* AI Provider Selection */}
        <Card>
          <CardHeader>
            <CardTitle>AI Image Provider</CardTitle>
            <CardDescription>
              Choose which AI service to use for generating product mockups
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Active Provider</Label>
              <Select value={currentProvider} onValueChange={handleProviderChange} disabled={saving}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((p) => (
                    <SelectItem key={p.id} value={p.id} disabled={!p.available}>
                      <div className="flex items-center gap-2">
                        {p.available ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500" />
                        )}
                        {p.name}
                        {!p.available && <span className="text-xs text-gray-400">(not configured)</span>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="pt-4 border-t">
              <h4 className="font-medium mb-2">Provider Status</h4>
              <div className="space-y-2">
                {providers.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span>{p.name}</span>
                    <Badge variant={p.available ? "default" : "destructive"}>
                      {p.available ? "Available" : "Not Configured"}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Model Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Model Configuration</CardTitle>
            <CardDescription>
              Select the specific model version for each provider
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Gemini Model */}
            <div>
              <Label className="flex items-center gap-2">
                Google Gemini Model
                {currentProvider === "gemini" && (
                  <Badge variant="outline" className="text-green-600">Active</Badge>
                )}
              </Label>
              <Select
                value={currentGeminiModel}
                onValueChange={(v) => handleModelChange("gemini", v)}
                disabled={saving}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.find((p) => p.id === "gemini")?.models.map((model) => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Gemini supports image inputs (logos, base product images)
              </p>
            </div>

            {/* BytePlus Model */}
            <div>
              <Label className="flex items-center gap-2">
                BytePlus Seedream Model
                {currentProvider === "byteplus" && (
                  <Badge variant="outline" className="text-green-600">Active</Badge>
                )}
              </Label>
              <Select
                value={currentByteplusModel}
                onValueChange={(v) => handleModelChange("byteplus", v)}
                disabled={saving}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.find((p) => p.id === "byteplus")?.models.map((model) => (
                    <SelectItem key={model} value={model}>
                      {model}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                Seedream is text-to-image only (no image inputs)
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Pricing Comparison</CardTitle>
            <CardDescription>
              Approximate costs per image generation
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium flex items-center gap-2">
                  Google Gemini
                  {currentProvider === "gemini" && <Badge>Active</Badge>}
                </h4>
                <p className="text-2xl font-bold mt-2">~$0.02 - $0.04</p>
                <p className="text-sm text-gray-500">per image</p>
                <ul className="mt-3 text-sm space-y-1 text-gray-600">
                  <li>+ Supports image inputs (logos)</li>
                  <li>+ Better text rendering</li>
                  <li>+ Complex instructions</li>
                </ul>
              </div>
              <div className="p-4 border rounded-lg">
                <h4 className="font-medium flex items-center gap-2">
                  BytePlus Seedream
                  {currentProvider === "byteplus" && <Badge>Active</Badge>}
                </h4>
                <p className="text-2xl font-bold mt-2">$0.03 - $0.04</p>
                <p className="text-sm text-gray-500">per image</p>
                <ul className="mt-3 text-sm space-y-1 text-gray-600">
                  <li>+ Fast generation</li>
                  <li>+ Good for product photos</li>
                  <li>- Text-only prompts</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
